/**
 * Replay files — a finished war saved for the film archive (`.friction-replay`).
 *
 *   bytes 0–7   "FRICTION"                       magic
 *   byte  8     container format (1)
 *   bytes 9–    gzip( MessagePack { header, log } )
 *
 * `log` is the engine's ReplayLog; `header` is what the archive shows before a film is screened
 * (theatre, nations, victors, length, when it was saved) plus what decides whether it can play:
 * the replay format version and the rules fingerprint of the engine that recorded it.
 *
 * Files may come from anyone, so reading one is as strict as reading a network frame: size caps
 * before and during decompression (a gzip bomb stops at `LIMITS.replayPayloadBytes`), standard
 * MessagePack only, decoded by the strict decoder (no extensions, no `__proto__`, bounded
 * nesting), then zod schemas and `checkReplayLog`. Plain MessagePack is also what gets written —
 * no msgpackr records, whose reader compiles code per record structure.
 */
import {
  checkReplayLog,
  type FactionId,
  type FactionPublic,
  REPLAY_VERSION,
  type ReplayLog,
  rulesFingerprint,
} from '@friction/engine';
import { Packr } from 'msgpackr';
import { LIMITS } from './limits';
import { decodeStrict } from './strict-msgpack';
import { replayFileHeaderSchema, replayLogSchema } from './validate';

export const REPLAY_FILE_EXTENSION = '.friction-replay';
/** ASCII "FRICTION". */
export const REPLAY_FILE_MAGIC: readonly number[] = [0x46, 0x52, 0x49, 0x43, 0x54, 0x49, 0x4f, 0x4e];
/** Container format written by this build; readers refuse newer ones. */
export const REPLAY_FILE_FORMAT = 1;

export interface ReplayFileHeader {
  /** Replay-log format of `log` (engine REPLAY_VERSION). */
  replayVersion: number;
  /** Rules fingerprint of the engine that recorded the war (engine `rulesFingerprint`). */
  rules: string;
  theatre: string;
  factions: { name: string; adjective: string; color: number; team: number; human: boolean }[];
  winners: FactionId[] | null;
  /** When the record was saved (ISO 8601). */
  savedAt: string;
  endTick: number;
  scenario: string | null;
}

export interface ReplayFile {
  header: ReplayFileHeader;
  log: ReplayLog;
}

export type ReplayFileErrorCode =
  /** Larger than the caps (file or decompressed payload). */
  | 'tooLarge'
  /** Not a FRICTION replay file at all. */
  | 'notReplay'
  /** A newer container format than this build reads. */
  | 'newer'
  /** A replay-log format this engine cannot play. */
  | 'version'
  /** Damaged: bad gzip or MessagePack. */
  | 'corrupt'
  /** Readable, but not a well-formed record. */
  | 'invalid';

export class ReplayFileError extends Error {
  override name = 'ReplayFileError';
  readonly code: ReplayFileErrorCode;
  constructor(code: ReplayFileErrorCode, message: string) {
    super(message);
    this.code = code;
  }
}

/** The header for a finished war: the log plus what the viewer knows of the theatre and nations. */
export function replayFileHeader(
  log: ReplayLog,
  theatre: string,
  factions: readonly Pick<FactionPublic, 'name' | 'adjective' | 'color' | 'team'>[],
  winners: FactionId[] | null,
  savedAt: Date,
): ReplayFileHeader {
  return {
    replayVersion: log.version,
    rules: rulesFingerprint(),
    theatre,
    factions: factions.map((f, i) => ({
      name: f.name,
      adjective: f.adjective,
      color: f.color,
      team: f.team,
      human: log.config.factions[i]?.controller === 'human',
    })),
    winners: winners ? winners.slice() : null,
    savedAt: savedAt.toISOString(),
    endTick: log.endTick,
    scenario: log.config.scenario ?? null,
  };
}

/** True when the file was recorded under exactly this engine's rules (else it may diverge). */
export const sameRules = (header: ReplayFileHeader): boolean => header.rules === rulesFingerprint();

// Standard MessagePack (no records, no extensions except msgpackr's `undefined`).
const plainPackr = new Packr({ useRecords: false });

/** Encode a replay file (magic, format, gzipped MessagePack). */
export async function encodeReplayFile(file: ReplayFile): Promise<Uint8Array<ArrayBuffer>> {
  // msgpackr returns a Buffer view on an ordinary (never shared) ArrayBuffer.
  const payload = plainPackr.pack({ header: file.header, log: file.log }) as Uint8Array<ArrayBuffer>;
  const gz = await pipeThrough(payload, new CompressionStream('gzip'), Number.POSITIVE_INFINITY);
  const out = new Uint8Array(REPLAY_FILE_MAGIC.length + 1 + gz.length);
  out.set(REPLAY_FILE_MAGIC);
  out[REPLAY_FILE_MAGIC.length] = REPLAY_FILE_FORMAT;
  out.set(gz, REPLAY_FILE_MAGIC.length + 1);
  return out;
}

/**
 * Read and validate an untrusted replay file. Throws a ReplayFileError; never anything else.
 * The rules fingerprint is NOT checked here — a film recorded under other rules may still be
 * screened (see `sameRules`; the viewer's seal reports whether it reproduced its war).
 */
export async function decodeReplayFile(bytes: Uint8Array): Promise<ReplayFile> {
  if (bytes.length > LIMITS.replayFileBytes)
    throw new ReplayFileError('tooLarge', `replay file of ${bytes.length} bytes exceeds the limit`);
  const head = REPLAY_FILE_MAGIC.length;
  if (bytes.length <= head || REPLAY_FILE_MAGIC.some((b, i) => bytes[i] !== b))
    throw new ReplayFileError('notReplay', 'not a FRICTION replay file');
  const format = bytes[head];
  if (format > REPLAY_FILE_FORMAT)
    throw new ReplayFileError('newer', `replay file format ${format} is newer than ${REPLAY_FILE_FORMAT}`);
  if (format < 1) throw new ReplayFileError('notReplay', `unknown replay file format ${format}`);

  const payload = await pipeThrough(
    // A copy on a plain ArrayBuffer, which the stream accepts whatever the caller's buffer was.
    new Uint8Array(bytes.subarray(head + 1)),
    new DecompressionStream('gzip'),
    LIMITS.replayPayloadBytes,
  );
  let raw: unknown;
  try {
    // header → factions → faction → field, log → entries → entry → c → [f, command] → unitIds → id
    raw = decodeStrict(payload, 10);
  } catch (err) {
    throw new ReplayFileError('corrupt', `unreadable replay payload: ${(err as Error).message}`);
  }
  const top = raw as { header?: { replayVersion?: unknown }; log?: { version?: unknown } } | null;
  // Version first: an older or newer log format need not match today's schema to be named.
  const version = top?.log?.version ?? top?.header?.replayVersion;
  if (typeof version === 'number' && version !== REPLAY_VERSION)
    throw new ReplayFileError(
      'version',
      `replay log version ${version} is not supported (expected ${REPLAY_VERSION})`,
    );
  const header = replayFileHeaderSchema.safeParse(top?.header);
  const log = replayLogSchema.safeParse(top?.log);
  if (!header.success || !log.success) {
    const issue = (header.success ? log : header).error?.issues[0];
    throw new ReplayFileError(
      'invalid',
      `malformed replay file${issue ? ` at ${issue.path.join('.') || '(root)'}: ${issue.message}` : ''}`,
    );
  }
  try {
    checkReplayLog(log.data);
  } catch (err) {
    throw new ReplayFileError('invalid', (err as Error).message);
  }
  if (
    header.data.endTick !== log.data.endTick ||
    header.data.factions.length !== log.data.config.factions.length ||
    header.data.replayVersion !== log.data.version
  )
    throw new ReplayFileError('invalid', 'the replay header does not match its log');
  return { header: header.data, log: log.data };
}

/**
 * Push `data` through a (de)compression stream and collect the output, stopping as soon as it
 * exceeds `limit` bytes. Stream failures (bad gzip, truncated data, trailing junk) become
 * `corrupt`.
 */
async function pipeThrough(
  data: Uint8Array<ArrayBuffer>,
  stream: CompressionStream | DecompressionStream,
  limit: number,
): Promise<Uint8Array> {
  const writer = stream.writable.getWriter();
  // Failures surface on the reading side; the writer's own promises must not go unhandled.
  writer
    .write(data)
    .then(() => writer.close())
    .catch(() => {});
  const reader = stream.readable.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      total += value.byteLength;
      if (total > limit)
        throw new ReplayFileError('tooLarge', `replay payload exceeds ${limit} bytes when decompressed`);
      chunks.push(value);
    }
  } catch (err) {
    await reader.cancel().catch(() => {});
    if (err instanceof ReplayFileError) throw err;
    throw new ReplayFileError('corrupt', `damaged replay data: ${(err as Error)?.message ?? String(err)}`);
  }
  const out = new Uint8Array(total);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.byteLength;
  }
  return out;
}
